/**
 * Software Validation Report Page
 * Generate, list, sign-off, and download SVRs.
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import QMSLayout from "../components/QMSLayout";
import {
  FileCheck, Plus, Download, CheckCircle2, Clock, Loader2,
  RefreshCw, PenLine, Shield, AlertTriangle
} from "lucide-react";

const STATUS_MAP = {
  pending_sign_off: { label: "Pending Sign-Off", color: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
  signed_off: { label: "Signed Off", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" },
};

export default function SVRPage() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [version, setVersion] = useState("");
  const [signingOff, setSigningOff] = useState(null);
  const isAdmin = user?.role === "admin";

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/svr/reports`, { credentials: "include" });
      const data = await res.json();
      setReports(data.reports || []);
    } catch { setReports([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!version.trim()) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API}/svr/generate?version=${encodeURIComponent(version)}`, {
        method: "POST", credentials: "include",
      });
      if (res.ok) {
        setShowGenerate(false);
        setVersion("");
        fetchReports();
      }
    } catch {}
    setGenerating(false);
  };

  const handleSignOff = async (svrId) => {
    setSigningOff(svrId);
    try {
      await fetch(`${API}/svr/${svrId}/sign-off`, { method: "POST", credentials: "include" });
      fetchReports();
    } catch {}
    setSigningOff(null);
  };

  const handleDownload = async (svrId) => {
    const res = await fetch(`${API}/svr/${svrId}/download`, { credentials: "include" });
    if (res.ok) {
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `SVR_${svrId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  return (
    <QMSLayout>
      <div className="space-y-4" data-testid="svr-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-white flex items-center gap-2">
              <FileCheck size={20} className="text-emerald-400" />
              Software Validation Reports
            </h1>
            <p className="text-xs text-[#8B95A5] mt-0.5">Auto-generated validation reports for platform releases, filed in Document Control</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchReports} className="p-2 rounded bg-[#1A1D27] border border-[rgba(255,255,255,0.07)] text-[#8B95A5] hover:text-white" data-testid="refresh-svr-btn">
              <RefreshCw size={14} />
            </button>
            {isAdmin && (
              <button onClick={() => setShowGenerate(!showGenerate)} className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all" data-testid="generate-svr-btn">
                <Plus size={12} />Generate SVR
              </button>
            )}
          </div>
        </div>

        {/* Generate Form */}
        {showGenerate && isAdmin && (
          <form onSubmit={handleGenerate} className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg p-4 flex items-end gap-3" data-testid="generate-svr-form">
            <div className="flex-1">
              <label className="text-xs text-[#8B95A5] mb-1 block">Platform Version Tag *</label>
              <input value={version} onChange={e => setVersion(e.target.value)} required placeholder="e.g. v2.4.0"
                className="w-full px-3 py-2 bg-[#1A1D27] border border-[rgba(255,255,255,0.07)] rounded text-sm text-white placeholder-[#555] focus:border-emerald-500/30 focus:outline-none" data-testid="svr-version-input" />
            </div>
            <button type="button" onClick={() => setShowGenerate(false)} className="px-4 py-2 text-sm text-[#8B95A5] hover:text-white">Cancel</button>
            <button type="submit" disabled={generating} className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded text-sm hover:bg-emerald-500/30 disabled:opacity-40" data-testid="svr-generate-submit">
              {generating ? <Loader2 size={14} className="animate-spin" /> : <FileCheck size={14} />}Generate
            </button>
          </form>
        )}

        {/* Reports List */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-emerald-400" /></div>
        ) : reports.length === 0 ? (
          <div className="text-center py-16" data-testid="no-svr-msg">
            <FileCheck size={40} className="text-[#333] mx-auto mb-3" />
            <p className="text-[#8B95A5] text-sm">No Software Validation Reports generated yet.</p>
            {isAdmin && <p className="text-[#555] text-xs mt-1">Click "Generate SVR" to create the first report.</p>}
          </div>
        ) : (
          <div className="space-y-3" data-testid="svr-list">
            {reports.map(r => {
              const st = STATUS_MAP[r.status] || STATUS_MAP.pending_sign_off;
              return (
                <div key={r.svr_id} className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg p-4" data-testid={`svr-${r.svr_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileCheck size={18} className="text-emerald-400" />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium text-sm">SVR — {r.version}</span>
                          <span className="text-[#8B95A5] font-mono text-xs">{r.svr_id}</span>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${st.color}`}>{st.label}</span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-[#8B95A5]">
                          <span className="flex items-center gap-1"><Clock size={10} />{r.generated_at?.slice(0, 10)}</span>
                          <span>by {r.generated_by_name}</span>
                          {r.sign_off && <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 size={10} />Signed by {r.sign_off.signed_by_name} on {r.sign_off.signed_at?.slice(0, 10)}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => handleDownload(r.svr_id)} className="flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-sky-500/10 text-sky-400 border border-sky-500/20 hover:bg-sky-500/20" data-testid={`download-svr-${r.svr_id}`}>
                        <Download size={12} />Download PDF
                      </button>
                      {isAdmin && r.status === "pending_sign_off" && (
                        <button onClick={() => handleSignOff(r.svr_id)} disabled={signingOff === r.svr_id}
                          className="flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/30 disabled:opacity-40" data-testid={`signoff-svr-${r.svr_id}`}>
                          {signingOff === r.svr_id ? <Loader2 size={12} className="animate-spin" /> : <PenLine size={12} />}Sign Off
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </QMSLayout>
  );
}
